<script lang="ts" setup>
import { PropType, warn, watch } from 'vue';
import {
  TirPmButton,
  TirPmButtonVariantEnum,
  TirPmButtonSizeEnum,
  TirPmPopover,
  PopoverColorEnum,
  PopoverPositionEnum,
  TirPmTooltipElement,
} from 'tir-components';
import { CrossCompactIcon, PlusIcon, StarIcon } from 'tir-style-system/icons/outline';
import { TaskViewElementEnum } from '@/components/tasks-kanban/enums';
import { OrderTaskModel, TaskColumnModel, TeammateModel } from '../../models';
import { TaskCard } from '../task-card';
import GetEmployeesWithAccessToProjectItemModel from '@/pages/monitorings/models/GetEmployeesWithAccessToProjectItemModel';
import { useRouter } from 'vue-router';

// #region defineProps
const props = defineProps({
  /**Юзер */
  user: { type: Object as PropType<TeammateModel> },
  /**Отображение иконки со звездой */
  showStar: {
    type: Boolean,
    default: false,
  },
  /**Задача */
  task: {
    type: Object as PropType<OrderTaskModel>,
    required: true,
  },
  /**Колонки */
  columns: {
    type: Array as PropType<Array<TaskColumnModel>>,
    default: () => [],
  },
  /**Настройки видимости */
  viewSettings: {
    type: Array as PropType<Array<TaskViewElementEnum>>,
    default: () => [],
  },
  /**Выбранный исполнитель */
  selectPerformer: { type: Object as PropType<GetEmployeesWithAccessToProjectItemModel> },
  /**Находится в очередности выполнения */
  atExecution: { type: Boolean },
  /**Таблица на странице мониторинга по тикетам */
  monitoringTicketPage: { type: Boolean, default: false },
  /**Скрыть пункты Открыть в шторке и Удалить */
  hideOpenAndDelete: { type: Boolean, default: false },
  /**Расположить статус в левой части таблицы */
  isStatusLeftBlock: { type: Boolean, default: false },
  /**Скрыть иконку у названия */
  hideIconName: { type: Boolean, default: false },
  /**Таблица на странице мониторинга */
  monitoringPage: { type: Boolean, default: false },
});
// #endregion defineProps

// #region defineEmits
const emit = defineEmits<{
  /**Событие добавления задачи в очередность */
  add: [task: OrderTaskModel];
  /**Событие удаления задачи из очередности */
  removeQueue: [task: OrderTaskModel];
  /**Событие открытия шторки задачи */
  openTaskDrawer: [taskId: number];
  /**Удаление задачи */
  removeTask: [taskId: number];
}>();
// #endregion defineEmits
const removeQueue = () => {
  emit('removeQueue', props.task);
};
</script>

<template>
  <TaskCard
    :user="user"
    :shortInfoParameter="true"
    :hideIconName="hideIconName"
    :task="task"
    :columns="columns"
    :view-settings="viewSettings"
    :slot-left-visible="showStar && atExecution"
    :monitoringPage="monitoringPage"
    :monitoringTicketPage="monitoringTicketPage"
    :selectPerformer="selectPerformer"
    :hideOpenAndDelete="hideOpenAndDelete"
    :isStatusLeftBlock="isStatusLeftBlock"
    @open-task-drawer="emit('openTaskDrawer', $event)"
    @removeTask="emit('removeTask', $event)"
  >
    <template #left>
      <div>
        <TirPmButton
          v-if="!showStar && task.IsHighlighted"
          class="project-tasks-li__button"
          :variant="TirPmButtonVariantEnum.SecondaryState"
          :size="TirPmButtonSizeEnum.Middle"
          @click="removeQueue"
        >
          <CrossCompactIcon class="project-tasks-li__button-icon" />1
        </TirPmButton>
        <TirPmTooltipElement v-else-if="showStar && atExecution" width="max-content">
          <div>
            <StarIcon class="project-tasks-li__check-icon" />
          </div>
          <template #content>
            <div class="project-tasks-li__tooltip-content">Добавлено в Очередь для выполнения</div>
          </template>
        </TirPmTooltipElement>
        <TirPmButton
          v-else
          class="project-tasks-li__button"
          :variant="TirPmButtonVariantEnum.SecondaryState"
          :size="TirPmButtonSizeEnum.Middle"
          @click="emit('add', task)"
        >
          <PlusIcon class="project-tasks-li__button-icon" />
        </TirPmButton>
      </div>
    </template>
  </TaskCard>
</template>

<style lang="scss" scoped>
.project-tasks-li {
  &__button {
    padding: 0.625rem 0;
    min-width: 2rem;
    height: 2rem;

    &-icon {
      width: 1rem;
      height: 1rem;
      color: var(--text-primary);
    }
  }
  &__check-icon {
    width: 1.5rem;
    height: 1.5rem;
    color: var(--text-secondary, #686c73);
  }
  &__tooltip-content {
    border-radius: 0.5rem;
    color: var(--text-primary-inverted, #e9eaeb);
    font-size: 0.75rem;
    font-weight: 400;
    line-height: 1rem;
  }
}
</style>
